import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
    Box, Card, CardContent, CardHeader, TextField, Button,
    Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Avatar, Divider,
    Stack, TablePagination, Paper, Grid, CircularProgress, Backdrop,
    Dialog, DialogTitle, DialogContent, DialogActions, Zoom
} from "@mui/material";
import { Edit, Delete, CloudUpload, Add, Search, Close } from "@mui/icons-material";

import {
    collection, addDoc, getDocs, deleteDoc,
    doc, updateDoc, serverTimestamp,
    orderBy, query, where, getDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";

/* ---------- Premium Counter Component ---------- */
const Counter = ({ label, value, min = 0, onChange }) => (
    <Box sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: "#f8fafc", borderRadius: "12px", px: 2, py: 1, height: 48,
        border: "1px solid #e2e8f0", transition: "all 0.2s",
        "&:hover": { borderColor: "#cbd5e1", background: "#f1f5f9" }
    }}>
        <Typography fontSize={13} fontWeight={600} color="text.secondary">{label} *</Typography>
        <Box sx={{
            display: "flex", alignItems: "center", background: "#fff",
            borderRadius: "8px", border: "1px solid #cbd5e1", p: 0.5
        }}>
            <IconButton size="small" sx={{ width: 28, height: 28, borderRadius: "6px" }}
                onClick={() => onChange(Math.max(min, value - 1))}>−</IconButton>
            <Box sx={{ width: 32, textAlign: "center", fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{value}</Box>
            <IconButton size="small" sx={{ width: 28, height: 28, borderRadius: "6px" }}
                onClick={() => onChange(value + 1)}>+</IconButton>
        </Box>
    </Box>
);

function Roomdetails() {
    const initialForm = {
        roomTypeName: "",
        cmId: "",
        description: "",
        adults: 2,
        children: 0,
        beds: 1,
        bedType: "",
        maxCmRoomCount: 1, // NEW: Added Max Channel Manager Room Count
        price: "",
        discount: 0,
        imageUrl: ""
    };

    const [form, setForm] = useState(initialForm);
    const [rooms, setRooms] = useState([]);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState("");
    const [editId, setEditId] = useState(null);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openModal, setOpenModal] = useState(false);

    const auth = getAuth();
    const [userData, setUserData] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUserData(user);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const fetchUserRole = async () => {
            if (!userData) return;
            try {
                const roleRef = doc(db, "authroles", userData.uid);
                const snap = await getDoc(roleRef);
                if (snap.exists()) {
                    const data = snap.data();
                    if (data.propertyId) {
                        setPropertyId(data.propertyId);
                    }
                }
            } catch (err) {
                console.error("Error fetching user metadata:", err);
            }
        };
        fetchUserRole();
    }, [userData]);

    const fetchRooms = async () => {
        if (!userData || !propertyId) return;
        try {
            const q = query(
                collection(db, "rooms"),
                where("propertyId", "==", propertyId),
                orderBy("roomTypeName", "asc")
            );
            const snap = await getDocs(q);
            const list = snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
            }));
            setRooms(list);
        } catch (err) {
            console.error("Error standardizing collection snap:", err);
        }
    };

    useEffect(() => {
        fetchRooms();
    }, [userData, propertyId]);

    /* ---------- Validation Engine ---------- */
    const validate = () => {
        let e = {};
        if (!form.roomTypeName.trim()) e.roomTypeName = "Room Type is explicit";
        if (!form.cmId.trim()) e.cmId = "Channel Manager ID parameter required";
        if (!form.bedType.trim()) e.bedType = "Structural assignment layout required";
        if (form.discount < 0 || form.discount > 100) e.discount = "Bound 0–100%";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            setErrors(prev => ({ ...prev, image: null }));
        }
    };

    const uploadImage = async () => {
        if (!imageFile) return form.imageUrl || "";
        const imgRef = ref(storage, `rooms/${Date.now()}_${imageFile.name}`);
        await uploadBytes(imgRef, imageFile);
        return await getDownloadURL(imgRef);
    };

    const handleOpenCreate = () => {
        setForm(initialForm);
        setImageFile(null);
        setImagePreview("");
        setEditId(null);
        setErrors({});
        setOpenModal(true);
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        if (!userData || !propertyId) {
            alert("Session profile missing architecture bounds.");
            return;
        }

        try {
            setIsSubmitting(true);
            const imageUrl = await uploadImage();
            const basePriceNum = Number(form.price);
            const discountNum = Number(form.discount);
            const finalPrice = basePriceNum - (basePriceNum * discountNum) / 100;

            const payload = {
                userId: userData.uid,
                propertyId: propertyId,
                roomTypeName: form.roomTypeName.trim(),
                cmId: form.cmId.trim(),
                description: form.description.trim(),
                maxCmRoomCount: Number(form.maxCmRoomCount), // INCLUDED IN PAYLOAD
                capacity: {
                    adults: form.adults,
                    children: form.children,
                    total: form.adults + form.children
                },
                bed: {
                    type: form.bedType.trim(),
                    count: form.beds
                },
                pricing: {
                    basePrice: basePriceNum,
                    discount: discountNum,
                    finalPrice: finalPrice
                },
                imageUrl,
                updatedAt: serverTimestamp()
            };

            if (editId) {
                await updateDoc(doc(db, "rooms", editId), payload);
            } else {
                await addDoc(collection(db, "rooms"), {
                    ...payload,
                    createdAt: serverTimestamp()
                });
            }

            setOpenModal(false);
            setForm(initialForm);
            setEditId(null);
            setImageFile(null);
            setImagePreview("");
            fetchRooms();
        } catch (error) {
            console.error("Submission operational failure:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (r) => {
        setForm({
            roomTypeName: r.roomTypeName || "",
            cmId: r.cmId || "",
            description: r.description || "",
            adults: r.capacity?.adults || 2,
            children: r.capacity?.children || 0,
            beds: r.bed?.count || 1,
            bedType: r.bed?.type || "",
            maxCmRoomCount: r.maxCmRoomCount || 1, // LOAD EXISTING MAX CM COUNT
            price: r.pricing?.basePrice || "",
            discount: r.pricing?.discount || 0,
            imageUrl: r.imageUrl || ""
        });
        setImagePreview(r.imageUrl || "");
        setEditId(r.id);
        setErrors({});
        setOpenModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Purge asset from local configuration tables?")) {
            try {
                setIsSubmitting(true);
                await deleteDoc(doc(db, "rooms", id));
                fetchRooms();
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const filteredRooms = rooms.filter(r =>
        r.roomTypeName?.toLowerCase().includes(search.toLowerCase()) ||
        r.cmId?.toLowerCase().includes(search.toLowerCase()) ||
        r.bed?.type?.toLowerCase().includes(search.toLowerCase())
    );

    const paginatedRooms = filteredRooms.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box display="flex" minHeight="100vh" bgcolor="#f8fafc">
            <Menu />

            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1301 }} open={isSubmitting}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress color="inherit" thickness={4.5} />
                    <Typography variant="body1" fontWeight={600} sx={{ letterSpacing: "0.5px" }}>Syncing Database Layers...</Typography>
                </Stack>
            </Backdrop>

            <Box flex={1} p={{ xs: 2, md: 4 }} className="content-wrapper" sx={{ maxWidth: "1600px", mx: "auto", width: "100%" }}>

                {/* HEADER MODULE */}
                <Box mb={2} display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={2}>
                    <Box>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" sx={{ letterSpacing: "-1px" }}>
                            Room Management
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<Add />}
                        onClick={handleOpenCreate}
                        sx={{
                            borderRadius: "12px",
                            px: 3,
                            py: 1.2,
                            fontWeight: 700,
                            textTransform: "none",
                            background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                            boxShadow: "0 4px 12px rgba(37,99,235,0.2)",
                            "&:hover": { background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)" }
                        }}
                    >
                        Provision New Type
                    </Button>
                </Box>

                {/* CENTRAL DATA METRICS VIEW */}
                <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 30px rgba(0,0,0,0.03)", border: "1px solid #e2e8f0", background: "#fff", overflow: "hidden" }}>
                    <CardHeader
                        title="Configured Room Matrix"
                        titleTypographyProps={{ fontSize: "16px", fontWeight: 700, color: "#1e293b" }}
                        action={
                            <TextField
                                size="small"
                                placeholder="Filter room types, CM ID..."
                                InputProps={{
                                    startAdornment: <Search fontSize="small" sx={{ color: "text.disabled", mr: 1 }} />,
                                }}
                                sx={{
                                    width: { xs: "100%", sm: 280 },
                                    bgcolor: "#f8fafc",
                                    borderRadius: "10px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" }
                                }}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        }
                        sx={{ py: 2.5, px: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, alignItems: { xs: "stretch", sm: "center" } }}
                    />
                    <Divider />

                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                        <Table size="small">
                            <TableHead sx={{ background: "#f8fafc" }}>
                                <TableRow>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569", width: 100 }}>Actions</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: "#475569", width: 80 }}>Index</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Classification Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: "#475569" }}>CM Reference ID</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Max CM Count</TableCell> {/* NEW TABLE HEADER */}
                                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Adults Bound</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Children Bound</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Beds Count</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Layout Spec</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>Visual Profile</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedRooms.map((r, index) => (
                                    <TableRow key={r.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, transition: "background 0.2s" }}>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton color="primary" size="small" onClick={() => handleEdit(r)} sx={{ bgcolor: "#eff6ff", borderRadius: "8px", '&:hover': { bgcolor: "#dbeafe" } }}>
                                                    <Edit fontSize="inherit" style={{ fontSize: "16px" }} />
                                                </IconButton>
                                                <IconButton color="error" size="small" onClick={() => handleDelete(r.id)} sx={{ bgcolor: "#fef2f2", borderRadius: "8px", '&:hover': { bgcolor: "#fee2e2" } }}>
                                                    <Delete fontSize="inherit" style={{ fontSize: "16px" }} />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>
                                            {page * rowsPerPage + index + 1}
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#1e293b" }}>
                                            {r.roomTypeName}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: "Monospace", color: "#2563eb", fontWeight: 600, fontSize: "13px" }}>
                                            {r.cmId || "—"}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700, color: "#0ea5e9" }}> {/* NEW TABLE CELL */}
                                            {r.maxCmRoomCount || "—"}
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: "#334155" }}>{r.capacity?.adults}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: "#334155" }}>{r.capacity?.children}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, color: "#334155" }}>{r.bed?.count}</TableCell>
                                        <TableCell sx={{ color: "#64748b", fontWeight: 500, fontSize: "13px" }}>{r.bed?.type || "—"}</TableCell>
                                        <TableCell align="center">
                                            {r.imageUrl ? (
                                                <Avatar src={r.imageUrl} variant="rounded" sx={{ width: 44, height: 44, border: "1px solid #e2e8f0", mx: "auto", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }} />
                                            ) : (
                                                <Typography variant="caption" color="text.disabled" fontWeight={600}>No Asset</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {filteredRooms.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                                No configuration definitions matching evaluation logic found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredRooms.length}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        sx={{ borderTop: "1px solid #e2e8f0" }}
                    />
                </Card>
            </Box>

            {/* POPUP PROVISIONING MODAL CONTAINER */}
            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                TransitionComponent={Zoom}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "20px",
                        boxShadow: "0 24px 50px rgba(0,0,0,0.15)",
                        border: "1px solid rgba(255,255,255,0.8)",
                        backdropFilter: "blur(20px)",
                        overflow: "hidden"
                    }
                }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bg: "#f8fafc", py: 2.5, px: 3 }}>
                    <Typography variant="h6" fontWeight={800} color="#0f172a">
                        {editId ? "🔧 Adjust Structural Properties" : "✨ Provision New Structural Type"}
                    </Typography>
                    <IconButton onClick={() => setOpenModal(false)} size="small" sx={{ color: "#64748b" }}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />

                <DialogContent sx={{ p: 3, bg: "#fff" }}>
                    <Grid className="row" spacing={2.5}>
                        <Grid className="col-sm-4 mb-4" item xs={12} sm={6}>
                            <TextField
                                size="small" label="Room Type Designation" fullWidth required
                                error={!!errors.roomTypeName} helperText={errors.roomTypeName}
                                value={form.roomTypeName}
                                onChange={e => setForm({ ...form, roomTypeName: e.target.value })}
                            />
                        </Grid>
                        <Grid className="col-sm-4 mb-4" item xs={12} sm={6}>
                            <TextField
                                size="small" label="Channel Manager ID (cmId)" fullWidth required
                                error={!!errors.cmId} helperText={errors.cmId}
                                value={form.cmId}
                                onChange={e => setForm({ ...form, cmId: e.target.value })}
                                placeholder="e.g. CM-ROOM-DX10"
                            />
                        </Grid>

                        {/* COUNTERS - Adjusted Grid sizes to md={3} to fit all 4 comfortably in one row on desktop */}
                        <Grid className="col-sm-4 mb-4" item xs={12} sm={6} md={3}>
                            <Counter label="Max CM Count" value={form.maxCmRoomCount} min={0} onChange={v => setForm({ ...form, maxCmRoomCount: v })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-4" item xs={12} sm={6} md={3}>
                            <Counter label="Beds Config" value={form.beds} min={1} onChange={v => setForm({ ...form, beds: v })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-4" item xs={12} sm={6} md={3}>
                            <Counter label="Max Adults" value={form.adults} min={1} onChange={v => setForm({ ...form, adults: v })} />
                        </Grid>
                        <Grid className="col-sm-4 mb-4" item xs={12} sm={6} md={3}>
                            <Counter label="Max Children" value={form.children} onChange={v => setForm({ ...form, children: v })} />
                        </Grid>

                        <Grid className="col-sm-4 mb-4" item xs={12} sm={4}>
                            <TextField
                                size="small" label="Bed Dimensions / Type" fullWidth required
                                error={!!errors.bedType} helperText={errors.bedType}
                                value={form.bedType}
                                onChange={e => setForm({ ...form, bedType: e.target.value })}
                                placeholder="e.g. King Suite"
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-4" item xs={12}>
                            <TextField
                                size="small" label="Inventory Profile Layout Description" multiline rows={3} fullWidth
                                error={!!errors.description} helperText={errors.description}
                                value={form.description}
                                onChange={e => setForm({ ...form, description: e.target.value })}
                            />
                        </Grid>

                        <Grid className="col-sm-4 mb-4" item xs={12}>
                            <Typography variant="body2" fontWeight={600} color={errors.image ? "error.main" : "text.secondary"} mb={1}>
                                Structural Asset Visual Profile * {errors.image && `(${errors.image})`}
                            </Typography>
                            <Stack direction="row" spacing={3} alignItems="center">
                                <Button
                                    component="label"
                                    variant="outlined"
                                    startIcon={<CloudUpload />}
                                    sx={{
                                        height: 56,
                                        borderStyle: "dashed",
                                        borderWidth: "2px",
                                        borderRadius: "12px",
                                        px: 3,
                                        textTransform: "none",
                                        borderColor: errors.image ? "#d32f2f" : "#cbd5e1",
                                        color: "#475569"
                                    }}
                                >
                                    Upload Architecture Image
                                    <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                                </Button>
                                {imagePreview && (
                                    <Avatar
                                        src={imagePreview}
                                        variant="rounded"
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            border: "2px solid #fff",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                            borderRadius: "12px"
                                        }}
                                    />
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
                <Divider />

                <DialogActions sx={{ p: 2.5, bg: "#f8fafc", gap: 1.5 }}>
                    <Button
                        onClick={() => setOpenModal(false)}
                        sx={{ textTransform: "none", fontWeight: 700, color: "#64748b", borderRadius: "10px" }}
                    >
                        Discard
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleSubmit}
                        sx={{
                            borderRadius: "10px",
                            px: 4,
                            py: 1,
                            fontWeight: 700,
                            textTransform: "none",
                            background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
                        }}
                    >
                        {editId ? "Update Specification" : "Commit Parameters"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Roomdetails;